import { Icon } from "@/components/msd/Icon";
import { AN_CARD, AN_H, AN_NUM_SM, AN_SUB, AN_T } from "@/components/msd/analytics";
import { bucketDaily, waitedLabel } from "@/lib/msdAnalytics";

// BS Intake vs JC Opening: bodyshop groups created against job cards sent to the
// group. A group being created is the car coming in; the job card going into it
// is the job card opened. The server sends counts; the wording and shape are here.

const num = (x) => Number(x || 0).toLocaleString("en-IN");
const plural = (n, one, many) => `${num(n)} ${n === 1 ? one : many}`;

const TH = "whitespace-nowrap border-0 border-b border-solid border-msd-line px-[6px] py-[10px] text-left text-[12.5px] font-semibold text-msd-muted";
const TD = "whitespace-nowrap border-0 border-b border-solid border-msd-line px-[6px] py-[10px]";
const NUM = "text-right tabular-nums";

function Tile({ label, value, sub, icon, color }) {
  return (
    <div className="min-w-0 rounded-2xl bg-msd-soft px-4 py-[14px]" data-testid={`msd-bs-tile-${label.toLowerCase().replace(/\W+/g, "-")}`}>
      <span className={`${AN_SUB} flex items-center gap-[6px]`}>
        {icon && (
          <span className="flex" style={{ color }}>
            <Icon name={icon} size={14} stroke={2.4} />
          </span>
        )}
        {label}
      </span>
      <span className={`${AN_NUM_SM} block`}>{value}</span>
      <span className={`${AN_SUB} block truncate`} title={sub}>
        {sub}
      </span>
    </div>
  );
}

function Spark({ days, counts }) {
  const bars = bucketDaily(days, counts);
  const m = Math.max(1, ...bars.map((b) => b.v));
  return (
    <span className="inline-flex h-6 items-end gap-[2px]" aria-hidden="true">
      {bars.map((b, i) => (
        <span
          key={i}
          className={`block rounded-[2px] bg-[var(--pd-fill)] ${bars.length > 20 ? "w-[3px]" : "w-[5px]"}`}
          style={{ height: `${b.v ? Math.max(3, (b.v / m) * 22) : 2}px`, opacity: b.v ? 1 : 0.35 }}
          title={`${b.full}: ${b.v}`}
        />
      ))}
    </span>
  );
}

export function BodyshopCard({ data, scopeLabel, workshopLabel = (x) => x, onOpenGroup, onExport }) {
  const t = data.total;
  const named = (key) => data.rows.filter((r) => r[key] > 0).map((r) => workshopLabel(r.location));
  const waitingAt = named("waiting");
  const nothing = t.groups_created === 0 && t.jobs_sent === 0;

  const inBars = bucketDaily(data.days, t.daily_in);
  const outBars = bucketDaily(data.days, t.daily_out);
  const mx = Math.max(1, ...inBars.map((b) => b.v), ...outBars.map((b) => b.v));
  const H = 140;
  const labels = inBars.length <= 16;
  const bar = (v, color) => (
    <span className="relative w-[min(12px,40%)] rounded-b-[3px] rounded-t-full" style={{ height: `${Math.max(3, (v / mx) * H)}px`, background: color }}>
      {labels && (
        <span className="absolute -top-[17px] left-1/2 -translate-x-1/2 text-[10.5px] font-semibold not-italic text-msd-muted">{v}</span>
      )}
    </span>
  );

  return (
    <section className={AN_CARD} data-testid="msd-bodyshop">
      <div className={`${AN_H} !items-center`}>
        <div>
          <div className={AN_T}>BS Intake vs JC Opening</div>
          <div className={AN_SUB}>Bodyshop groups created and job cards sent to the group · {scopeLabel}</div>
        </div>
        <button
          type="button"
          onClick={onExport}
          data-testid="msd-bodyshop-export"
          className="inline-flex h-[38px] items-center gap-[7px] rounded-xl border-0 bg-msd-surface px-[14px] text-[14px] font-semibold text-msd-ink shadow-msd-e1 hover:bg-msd-soft"
        >
          <Icon name="doc" size={16} />
          Export
        </button>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 max-[860px]:grid-cols-1">
        <Tile label="Groups created" value={num(t.groups_created)} sub="bodyshop jobs" />
        <Tile label="Job cards sent" value={num(t.jobs_sent)} sub="to the WhatsApp group" />
        <Tile
          label="Cars without a job card"
          value={num(t.waiting)}
          sub={waitingAt.length ? `at ${waitingAt.join(", ")}` : "none waiting"}
          icon="warn"
          color="#F79009"
        />
      </div>

      <div className="mt-[14px] flex items-start gap-2 rounded-xl bg-msd-soft px-3 py-[10px] text-[13.5px] text-msd-ink2" data-testid="msd-bodyshop-insight">
        <span className="mt-px shrink-0 text-[#3A5F94]">
          <Icon name="info" size={16} stroke={2} />
        </span>
        <span>
          {nothing ? (
            <>No bodyshop cars in this period.</>
          ) : (
            <>
              {plural(t.groups_created, "group", "groups")} created and {plural(t.jobs_sent, "job card", "job cards")} sent to the group.{" "}
              {t.waiting > 0 ? (
                <b>{plural(t.waiting, "car is", "cars are")} still waiting for a job card.</b>
              ) : (
                <>Every car that came in has its job card.</>
              )}
            </>
          )}
        </span>
      </div>

      <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-6 max-[1100px]:grid-cols-1">
        <div>
          <div className={`${AN_SUB} mb-1 mt-[18px] font-semibold text-msd-ink`}>Groups created and cars delivered each day</div>
          <div
            className="flex h-[190px] items-end gap-[6px] border-0 border-b border-solid border-msd-line pt-5"
            role="img"
            aria-label="Groups created and cars delivered each day"
          >
            {inBars.map((b, i) => (
              <div
                key={i}
                className="flex min-w-0 flex-1 basis-0 flex-col justify-end"
                title={`${b.full}: ${b.v} created, ${outBars[i]?.v ?? 0} delivered`}
              >
                <div className="flex items-end justify-center gap-[3px]">
                  {bar(b.v, "var(--pd-fill)")}
                  {bar(outBars[i]?.v ?? 0, "#8FA6E8")}
                </div>
                <div className={`${AN_SUB} mt-[6px] text-center !text-[12px]`}>{b.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-4 text-[13px] text-msd-muted">
            <span className="flex items-center gap-[6px]">
              <span className="inline-block h-[10px] w-[10px] rounded-[3px] bg-[var(--pd-fill)]" />
              Groups created
            </span>
            <span className="flex items-center gap-[6px]">
              <span className="inline-block h-[10px] w-[10px] rounded-[3px] bg-[#8FA6E8]" />
              Cars delivered
            </span>
          </div>
        </div>

        <div>
          <div className={`${AN_SUB} mb-2 mt-[18px] flex items-center justify-between font-semibold text-msd-ink`}>
            <span>Waiting for a job card now</span>
            <span className="inline-flex h-6 items-center rounded-full bg-[color-mix(in_srgb,#F79009_16%,transparent)] px-[10px] text-[12px] font-semibold text-[#B25E00]" data-testid="msd-bodyshop-waiting-count">
              {data.waiting_now.length}
            </span>
          </div>
          <div className="flex flex-col overflow-hidden rounded-[14px] shadow-msd-e1" data-testid="msd-bodyshop-waiting">
            {data.waiting_now.length === 0 ? (
              <div className={`${AN_SUB} px-3 py-4`}>No bodyshop cars are waiting for a job card.</div>
            ) : (
              data.waiting_now.map((w) => (
                <button
                  key={w.group_row_id}
                  type="button"
                  onClick={() => onOpenGroup(w)}
                  data-testid="msd-bodyshop-waiting-row"
                  className="flex w-full cursor-pointer items-center gap-[10px] border-0 border-t border-solid border-msd-line bg-msd-surface px-3 py-[10px] text-left font-[inherit] first:border-t-0 hover:bg-msd-soft"
                >
                  <span className="min-w-0 grow">
                    <b className="block truncate font-mono text-[13.5px] tracking-[.03em] text-msd-ink">{w.plate || "No plate"}</b>
                    <span className={`${AN_SUB} block truncate`}>
                      {[workshopLabel(w.workshop), w.manager].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <span
                    className={`whitespace-nowrap text-[13px] ${w.hours > data.on_time_hours ? "font-semibold text-[#E0715E]" : "text-msd-muted"}`}
                    title={w.hours > data.on_time_hours ? `More than ${data.on_time_hours} hours` : undefined}
                  >
                    {waitedLabel(w.hours)}
                  </span>
                  <Icon name="right" size={15} stroke={2.2} color="var(--faint)" />
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="mt-[18px] overflow-x-auto">
        <table className="w-full min-w-[720px] border-separate border-spacing-0 text-[14.5px]">
          <thead>
            <tr>
              <th className={TH}>Workshop</th>
              <th className={`${TH} ${NUM}`}>Groups created</th>
              <th className={`${TH} ${NUM}`}>Job cards sent</th>
              <th className={`${TH} ${NUM}`}>Waiting for a job card</th>
              <th className={`${TH} ${NUM}`}>Delivered</th>
              <th className={TH}>Daily groups created</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => {
              const empty = !r.groups_created && !r.jobs_sent && !r.delivered;
              return (
                <tr key={r.location} className={empty ? "text-msd-faint" : ""}>
                  <th scope="row" className={`${TD} text-left font-medium`}>
                    {workshopLabel(r.location)}
                    {r.manager && <span className={`${AN_SUB} block font-normal`}>{r.manager}</span>}
                  </th>
                  <td className={`${TD} ${NUM}`}>{num(r.groups_created)}</td>
                  <td className={`${TD} ${NUM}`}>{num(r.jobs_sent)}</td>
                  <td className={`${TD} ${NUM}`}>
                    {r.waiting > 0 ? (
                      <span className="inline-flex items-center gap-1 font-bold text-msd-ink">
                        <span className="flex text-[#F79009]" role="img" aria-label="Waiting">
                          <Icon name="warn" size={12} stroke={2.4} />
                        </span>
                        {r.waiting}
                      </span>
                    ) : (
                      <span className={AN_SUB}>0</span>
                    )}
                  </td>
                  <td className={`${TD} ${NUM}`}>{num(r.delivered)}</td>
                  <td className={TD}>{empty ? <span className={AN_SUB}>No bodyshop cars</span> : <Spark days={data.days} counts={r.daily_in} />}</td>
                </tr>
              );
            })}
            <tr className="font-bold">
              <th scope="row" className={`${TD} border-b-0 border-t text-left font-[inherit]`}>
                All bodyshops
              </th>
              <td className={`${TD} ${NUM} border-b-0 border-t`}>{num(t.groups_created)}</td>
              <td className={`${TD} ${NUM} border-b-0 border-t`}>{num(t.jobs_sent)}</td>
              <td className={`${TD} ${NUM} border-b-0 border-t`}>{num(t.waiting)}</td>
              <td className={`${TD} ${NUM} border-b-0 border-t`}>{num(t.delivered)}</td>
              <td className={`${TD} border-b-0 border-t`} />
            </tr>
          </tbody>
        </table>
      </div>

      <div className={`${AN_SUB} mt-3`}>
        A car waiting more than {data.on_time_hours} hours for its job card is shown in red.
      </div>
    </section>
  );
}

export const downloadBodyshopCsv = (csv, name) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};
